/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/lib/assist/buildStep.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)   | Who               | Why                       | What Changed          |
 * |---|---------------------|-------------------|---------------------------|------------------------|
 * | 1 | 2026-07-06          | Claude Sonnet 5   | AI Assist port (from Plex)| Created — converts a  |
 * |   |                     |                   |                           | parsed AssistIntent + |
 * |   |                     |                   |                           | resolved element into |
 * |   |                     |                   |                           | ordinary IRStep(s),   |
 * |   |                     |                   |                           | delegating selector/  |
 * |   |                     |                   |                           | fingerprint building  |
 * |   |                     |                   |                           | to recorder.ts's      |
 * |   |                     |                   |                           | buildSelector()       |
 * | 2 | 2026-07-06          | Claude Sonnet 5   | pressKey had no commit    | Added case "pressKey" |
 * |   |                     |                   | path (Plex's own grammar  | to the no-resolution  |
 * |   |                     |                   | has the same gap — parses | switch — press-key    |
 * |   |                     |                   | but was never wired to a  | intents need no       |
 * |   |                     |                   | buildable step)           | element resolution    |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/components/AssistBar.tsx
 * - apps/studio/src/lib/assist/__tests__/buildStep.test.ts
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - buildAssistSteps        — (intent, resolved, tree, screenSize, manualSelector?) -> IRStep[] | null
 * - buildSelectorFromMatch  — (match, tree, screenSize) -> Selector (exported for tests)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Ported from D:\Plex\apps\studio-web\src\lib\assist\buildStep.ts. This is
 *    the COMMIT step of the AI Assist pipeline. The output is a 100%-ordinary
 *    IRStep — nothing downstream (runner, reporter, healing) needs to know the
 *    step originated from a typed sentence. Candidates are tagged
 *    `addedBy:'assist'` purely for audit-trail purposes.
 * 2. UNLIKE Plex, selector + fingerprint construction is NOT reimplemented
 *    here — it delegates to recorder.ts's `buildSelector(node, flatNodes,
 *    ancestors, tapPoint, screenSize)`, the exact same uniqueness-aware
 *    candidate builder + Tier-1 fingerprint computer Drive-mode tap recording
 *    already uses. Every candidate buildSelector produces is tagged
 *    `addedBy:"recorder"` by default; buildSelectorFromMatch remaps every
 *    candidate to `addedBy:"assist"` afterward — zero edits needed to
 *    recorder.ts's shared candidate-building logic.
 * 3. `typeText` has NO required selector field in the IR schema
 *    (packages/ir/src/schemas/steps.ts) — it always types into whatever is
 *    currently focused. So "type X into the Email field" compiles to TWO
 *    steps: [tap(Email field), typeText(X)], mirroring how Drive-mode
 *    recording already captures click-then-type. "type X" with no target
 *    compiles to a single typeText step (relies on whatever is already
 *    focused, e.g. immediately after a manual tap).
 * 4. Four intents (openUrl, wait, screenshot, assertPageContainsText) need NO
 *    element resolution at all — "page contains X" maps directly to a
 *    `textContains` candidate + assertion:'visible', with no live-tree lookup
 *    required (the runner's resolver searches the whole flattened tree by
 *    default, same as any other textContains candidate).
 * 5. Every other intent requires a `resolved` ResolvedMatch + the live `tree`
 *    (for ancestor/fingerprint context) — returns null if either is missing,
 *    which the caller (AssistBar) treats as "not ready to confirm yet".
 * 6. Mimiq's IRStep schema has NO `label` field (unlike Plex's) — the
 *    human-readable description lives only in `selector.description`,
 *    consistent with how Drive-mode-recorded steps already work.
 * =============================================================================
 */

import { generateId } from "@mimiq/ir";
import type { IRStep, Selector, Candidate } from "@mimiq/ir";
import type { A11yNode } from "../../device/treeParser.js";
import { buildSelector, flattenTree, buildParentMap, getAncestors } from "../../device/recorder.js";
import type { AssistIntent } from "./grammar.js";
import type { ResolvedMatch } from "./resolver.js";

/**
 * Builds a Selector from a resolved A11yNode by delegating to recorder.ts's
 * buildSelector (uniqueness-aware candidates + Tier-1 fingerprint), then
 * remapping every resulting candidate's addedBy to "assist".
 */
export function buildSelectorFromMatch(
  match: ResolvedMatch,
  tree: A11yNode[],
  screenSize: { width: number; height: number },
): Selector {
  const flat = flattenTree(tree);
  const parentMap = buildParentMap(tree);
  const ancestors = getAncestors(match.node, parentMap);

  const auto = buildSelector(match.node, flat, ancestors, undefined, screenSize);

  return {
    ...auto,
    candidates: auto.candidates.map((c) => ({ ...c, addedBy: "assist" as const })),
  };
}

/**
 * Converts a parsed AssistIntent (+ a resolved element, when the intent needs one)
 * into one or more ordinary IRStep objects. Returns null when the intent cannot
 * yet be committed (e.g. an element-targeting intent with no resolved match).
 *
 * `manualSelector` is the optional "🎯 Pick element" enrichment: when the user
 * manually picks an element, its candidates are prepended (tagged
 * `addedBy:'user'`) onto the auto-resolved selector's candidates for extra
 * fallback strength. When there is NO `resolved` match at all (e.g. Lane 1
 * found nothing), `manualSelector` becomes the sole source of candidates
 * instead of a merge.
 */
export function buildAssistSteps(
  intent: AssistIntent,
  resolved: ResolvedMatch | null,
  tree: A11yNode[] | null,
  screenSize: { width: number; height: number },
  manualSelector?: Selector | null,
): IRStep[] | null {
  const raw = intent.raw;

  // ── Intents that need no element resolution ────────────────────────────────
  switch (intent.kind) {
    case "openUrl": {
      if (!intent.url) return null;
      return [{ id: generateId(), type: "openUrl", url: intent.url }];
    }
    case "wait": {
      if (intent.seconds === undefined) return null;
      return [{ id: generateId(), type: "wait", durationMs: Math.round(intent.seconds * 1000) }];
    }
    case "screenshot": {
      return [{ id: generateId(), type: "screenshot" }];
    }
    case "pressKey": {
      if (!intent.key) return null;
      return [{ id: generateId(), type: "pressKey", key: intent.key }];
    }
    case "assertPageContainsText": {
      if (!intent.text) return null;
      const selector: Selector = {
        description: raw,
        candidates: [{ strategy: "textContains", value: intent.text, addedBy: "assist" }],
      };
      return [{ id: generateId(), type: "assert", selector, assertion: "visible" }];
    }
    case "typeText": {
      // Only handle the no-target form here; a targeted typeText falls through
      // to the resolved-element branch below (needs a preceding tap-to-focus).
      if (!intent.target) {
        if (intent.text === undefined) return null;
        return [{ id: generateId(), type: "typeText", text: intent.text }];
      }
      break;
    }
    default:
      break;
  }

  // ── Everything else requires a resolved element+tree, a manual pick, or both ─
  if (!manualSelector && (!resolved || !tree)) return null;

  const selector: Selector = (() => {
    const manualCandidates: Candidate[] = manualSelector
      ? manualSelector.candidates.map((c) => ({ ...c, addedBy: "user" as const }))
      : [];

    if (resolved && tree) {
      const auto = buildSelectorFromMatch(resolved, tree, screenSize);
      return manualCandidates.length > 0
        ? { ...auto, candidates: [...manualCandidates, ...auto.candidates] }
        : auto;
    }
    // No live resolution at all — the manual pick is the only source.
    return { description: raw, candidates: manualCandidates };
  })();

  switch (intent.kind) {
    case "tap":
      return [{ id: generateId(), type: "tap", selector }];
    case "doubleTap":
      return [{ id: generateId(), type: "doubleTap", selector }];
    case "longPress":
      return [{ id: generateId(), type: "longPress", selector, durationMs: 500 }];
    case "clearText":
      return [{ id: generateId(), type: "clearText", selector }];
    case "waitForVisible":
      return [{ id: generateId(), type: "waitFor", selector, state: "visible" }];
    case "scrollUntilVisible":
      return [{ id: generateId(), type: "scrollUntilVisible", selector, direction: "down", maxScrolls: 10 }];
    case "assertVisible":
      return [{ id: generateId(), type: "assert", selector, assertion: "visible" }];
    case "assertNotVisible":
      return [{ id: generateId(), type: "assert", selector, assertion: "notVisible" }];
    case "assertEnabled":
      return [{ id: generateId(), type: "assert", selector, assertion: "enabled" }];
    case "assertDisabled":
      return [{ id: generateId(), type: "assert", selector, assertion: "disabled" }];
    case "assertHasText": {
      if (!intent.text) return null;
      return [{ id: generateId(), type: "assert", selector, assertion: "hasText", value: intent.text }];
    }
    case "typeText": {
      // Reached only when intent.target was set — tap to focus, then type.
      if (intent.text === undefined) return null;
      const tapStep: IRStep = { id: generateId(), type: "tap", selector };
      const typeStep: IRStep = { id: generateId(), type: "typeText", text: intent.text };
      return [tapStep, typeStep];
    }
    default:
      return null;
  }
}
